import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { completeInvite } from '@gen2/api/invite-listing/api';
import { InviteListingKeys } from '@gen2/api/invite-listing/hooks';
import { useDuplicateInvite } from '@gen2/app/invite-listing/hook/useDuplicateInvite';
import { useToast } from '@gen2/hooks';
import { PermissionsKeys } from '@gen2/types/permissions';
import { Permissions } from '@gen2/utils/permissions/permissions';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useInviteListingStore } from '../../store';

export const Actions = ({
  id,
  subject,
  status,
}: {
  id: string;
  subject: string;
  status: string;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { setArchive } = useInviteListingStore();
  const { handleDuplicate, isLoading } = useDuplicateInvite();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();

  const handleOnClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleArchive = () => {
    setArchive({ isOpen: true, subject, id });
    handleOnClose();
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/invite-listing/${id}`,
    );
    handleOnClose();
  };

  const handleViewInvite = () => {
    window.open(`/invite-listing/${id}`, '_blank');
    handleOnClose();
  };

  const handleEditInvite = () => {
    window.open(`/send-invites/${id}`, '_blank');
    handleOnClose();
  };

  const handleCompleteInvite = async () => {
    try {
      setLoading(true);
      const { data } = await completeInvite(id);

      // Invalidate the invite listing query to refresh the data
      await queryClient.invalidateQueries({
        queryKey: [InviteListingKeys.queryInviteList],
      });

      toast.show({
        text: data?.message ?? 'Invite marked as completed',
        variant: 'success',
      });
    } catch {
      toast.show({
        text: "Couldn't mark invite as completed",
        variant: 'error',
      });
    } finally {
      setLoading(false);
      handleOnClose();
    }
  };

  return (
    <div>
      <IconButton data-cy="ellipsis-button" onClick={handleClick}>
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleOnClose}
      >
        <MenuItem onClick={handleCopyToClipboard}>Copy Invite Link</MenuItem>
        <MenuItem onClick={handleViewInvite}>View</MenuItem>
        <Permissions allow={PermissionsKeys.UPDATE_INVITE}>
          <MenuItem onClick={handleEditInvite}>Edit Invite</MenuItem>
        </Permissions>
        <Permissions allow={PermissionsKeys.DUPLICATE_INVITE}>
          <MenuItem
            data-cy="duplicate"
            onClick={() => {
              handleDuplicate(id);
              handleOnClose();
            }}
            disabled={isLoading}
          >
            Duplicate Invite
          </MenuItem>
        </Permissions>
        <Permissions allow={PermissionsKeys.ARCHIVE_INVITE}>
          <MenuItem onClick={handleArchive}>Archive Invite</MenuItem>
        </Permissions>
        <Permissions allow={PermissionsKeys.COMPLETE_INVITE}>
          <MenuItem
            onClick={handleCompleteInvite}
            disabled={loading || status === 'completed'}
          >
            Mark as Completed
          </MenuItem>
        </Permissions>
      </Menu>
    </div>
  );
};
